// Hook personnalisé pour gérer les opérations CRUD avec localStorage
import { useState, useEffect } from 'react';
import localStorageService from './localStorageService';

/**
 * Hook personnalisé pour gérer un état avec persistance dans le localStorage
 * 
 * @param {string} entityName - Nom de l'entité à gérer (ex: 'students', 'courses')
 * @param {boolean} autoInit - Deprecated parameter, no longer used (only real data is loaded)
 * @returns {Object} Méthodes et état pour gérer l'entité
 */
export function useLocalStorage(entityName, autoInit = true) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les données initiales (real data only)
  useEffect(() => {
    try {
      // No mock data initialization - only load real data
      const data = localStorageService.getAllItems(entityName);
      setItems(data);
      setIsLoading(false);
    } catch (err) {
      console.error(`Erreur lors du chargement des données ${entityName}:`, err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [entityName]);

  // Obtenir un élément par ID
  const getById = (id) => {
    return localStorageService.getItemById(entityName, id);
  };

  // Ajouter un nouvel élément
  const addItem = (item) => {
    try {
      const newItem = localStorageService.addItem(entityName, item);
      setItems(prev => [...prev, newItem]);
      return newItem;
    } catch (err) {
      console.error(`Erreur lors de l'ajout d'un élément ${entityName}:`, err);
      setError(err.message);
      return null;
    }
  };

  // Mettre à jour un élément existant
  const updateItem = (item) => {
    try {
      const updatedItem = localStorageService.updateItem(entityName, item);
      setItems(prev => prev.map(i => i.id === item.id ? updatedItem : i));
      return updatedItem;
    } catch (err) {
      console.error(`Erreur lors de la mise à jour d'un élément ${entityName}:`, err);
      setError(err.message);
      return null;
    }
  };

  // Supprimer un élément
  const deleteItem = (id) => {
    try {
      const success = localStorageService.deleteItem(entityName, id);
      if (success) {
        setItems(prev => prev.filter(item => item.id !== id));
      }
      return success;
    } catch (err) {
      console.error(`Erreur lors de la suppression d'un élément ${entityName}:`, err);
      setError(err.message);
      return false;
    }
  };

  // Actualiser les données
  const refreshData = () => {
    try {
      const data = localStorageService.getAllItems(entityName);
      setItems(data);
      setError(null);
    } catch (err) {
      console.error(`Erreur lors de l'actualisation des données ${entityName}:`, err);
      setError(err.message);
    }
  };

  return {
    items,
    isLoading,
    error,
    getById,
    addItem,
    updateItem,
    deleteItem,
    refreshData
  };
}

export default useLocalStorage;